import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompanyID from "@/app/components/common/actions/inputs-b/CompanyID";
import {
  gxgrowxcompanycheckApi,
  gxgrowxstatusupdateApi,
} from "@/app/apiConfig/services/authAbstractApiLayer";
import { redirectToGrowXLogout } from "@/app/utils/loginservice/redirectToGrowXLogout";
import { useToken } from "@/app/services/context/TokenContext";
import { DATA_GIF_PATH, ICON_BASE_PATH } from "@/app/utils/config";
import { ContactSupportContent } from "@/app/services/context/ContactSupportContent";

const ERROR_CODE_LIMIT_FOR_COMPANY = 970;
const ERROR_CODE_COMPANY_LIMIT = 969;

type LimitContent = { title: string; message: string };

const LIMIT_CONTENT: Record<number, LimitContent> = {
  [ERROR_CODE_LIMIT_FOR_COMPANY]: {
    title: "Maximum company request limit reached.",
    message: "The maximum company request limit reached.",
  },
  [ERROR_CODE_COMPANY_LIMIT]: {
    title: "Maximum company request limit reached.",
    message: "The maximum company request limit reached.",
  },
};

const ERROR_MESSAGE_LIMIT_MAP: Record<string, number> = {
  MAX_REQUEST_LIMIT_REACHED_FOR_COMPANY: ERROR_CODE_LIMIT_FOR_COMPANY,
  MAX_COMPANY_REQUEST_LIMIT_REACHED: ERROR_CODE_COMPANY_LIMIT,
};

const getLimitErrorCode = (err: any): number | null => {
  const rawCode =
    err?.errorCode ?? err?.cause?.errorCode ?? err?.response?.data?.errorCode;
  const errCode = String(rawCode ?? "");
  if (
    errCode === String(ERROR_CODE_LIMIT_FOR_COMPANY) ||
    errCode === String(ERROR_CODE_COMPANY_LIMIT)
  ) {
    return Number(errCode);
  }
  const msg = String(err?.message ?? "").trim();
  if (msg in ERROR_MESSAGE_LIMIT_MAP) {
    return ERROR_MESSAGE_LIMIT_MAP[msg];
  }
  return null;
};

const CompanyAccess = () => {
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("userIdIDP");
  const [companyId, setCompanyId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const { logout } = useToken();
  const [isVerified, setIsVerified] = useState(false);
  const [limitErrorCode, setLimitErrorCode] = useState<number | null>(null);
  const [companyLimitReached, setCompanyLimitReached] = useState(false);
  const [companyLimitReached1, setCompanyLimitReached1] = useState(false);

  const handleContinue = async () => {
    if (!companyId.trim()) {
      setError("Company ID is required");
      setSuccess("");
      return;
    }
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setLimitErrorCode(null);
      setCompanyLimitReached(false);

      const payload = {
        userId,
        groupId: companyId,
      };

      await gxgrowxcompanycheckApi(payload);

      setSuccess("GrowX Company ID verified successfully");
      setError("");
      setIsVerified(true);
    } catch (err: any) {
      const limitCode = getLimitErrorCode(err);

      if (limitCode === ERROR_CODE_COMPANY_LIMIT) {
        setSuccess("");
        setError("");
        setIsVerified(false);
        setCompanyLimitReached(true);
      } else if (limitCode !== null) {
        setSuccess("");
        setError("");
        setIsVerified(false);
        setLimitErrorCode(limitCode);
      } else {
        setSuccess("");
        setError("Invalid Company ID");
        setIsVerified(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlerequest = async () => {
    try {
      setLoading(true);
      setLimitErrorCode(null);
      setCompanyLimitReached(false);
      const payload = {
        userId,
      };

      await gxgrowxstatusupdateApi(payload);

      navigate("/onboarding/requestSentSuccessfully");
    } catch (err: any) {
      const limitCode = getLimitErrorCode(err);

      if (limitCode === ERROR_CODE_COMPANY_LIMIT) {
        setSuccess("");
        setError("");
        setCompanyLimitReached(true);
      } else if (limitCode !== null) {
        setSuccess("");
        setError("");
        setLimitErrorCode(limitCode);
      } else {
        console.error(err);
        setError("Unable to process request");
      }
    } finally {
      setLoading(false);
    }
  };

  // ..................................handleLogout.......................................
  const handleLogout = () => {
    setTimeout(redirectToGrowXLogout, 0);
    logout();
    setLimitErrorCode(null);
    setCompanyLimitReached(false);
  };

  const handleLimitContinue = () => {
    setLimitErrorCode(null);
    setCompanyLimitReached(false);
    setCompanyLimitReached1(true);
    setCompanyId("");
    setIsVerified(false);
    setSuccess("");
    setError("");
  };

  const limitContent: LimitContent | null = limitErrorCode
    ? LIMIT_CONTENT[limitErrorCode]
    : companyLimitReached
      ? LIMIT_CONTENT[ERROR_CODE_COMPANY_LIMIT]
      : null;

  const showLimitScreen = Boolean(limitContent) || companyLimitReached;

  return (
    <div className="gx-smeonboard-card">
      <div className="gx-smeonboard-child">
        {/* .............main form box............... */}
        {showLimitScreen ||
          (!companyLimitReached1  && (
            <div className="gx-smeonboard-box">
              <div className="gx-companyaccess-box">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12">
                    <div className="gx-request-progress-title">
                      <h2 className="gx-h2-l">Lets Confirm</h2>
                      <span className="gx-h2-b">Your Company Access</span>
                      <p className="gx-p12-m">
                        You'll need your GrowX Company ID to continue
                      </p>
                    </div>
                  </div>

                  <div className="col-span-12">
                    <CompanyID
                      label="Company ID"
                      value={companyId}
                      onChange={(val) => {
                        setCompanyId(val);
                        if (companyLimitReached) setCompanyLimitReached(false);
                      }}
                      validationMessage={error}
                    />

                    {success && (
                      <div className="gx_input_successmessage">{success}</div>
                    )}
                  </div>

                  {!success && (
                    <div className="col-span-12">
                      <div className="growx-company-box">
                        <h4 className="gx-h4-sb">
                          Don't have your GrowX Company ID?
                        </h4>
                        <p className="gx-p12-m">
                          Please enquire with your Company Owner or Manager to
                          get your Company ID or if this is first time
                          onboarding to
                        </p>
                        <ContactSupportContent variant="inline" />
                      </div>
                    </div>
                  )}

                  <div className="col-span-12 mt-3">
                    <div className="login-continue-btn request-bnt">
                      <button
                        className="gx-h4-eb"
                        onClick={isVerified ? handlerequest : handleContinue}
                        disabled={loading}
                      >
                        {loading
                          ? "PROCESSING..."
                          : isVerified
                            ? "Request Owner/Manager for approval"
                            : "Continue"}
                      </button>
                    </div>
                  </div>
                  <div className="col-span-12">
                    <div className="gx-connectbank-logout">
                      <button onClick={handleLogout}>
                        <img
                          src={`${ICON_BASE_PATH}logouticon.svg`}
                          alt="name"
                        />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* ..................max limit reached................... */}
        {showLimitScreen && limitContent && (
          <div className="gx-smeonboard-box">
            <div className="gx-CompanyIdVerity-box">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <div className="gx-login-error-img">
                    <img
                      src={`${DATA_GIF_PATH}reject_animation.gif`}
                      alt="Error"
                    />
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="gx-request-progress-title">
                    <h2 className="gx-h2-26-l">{limitContent.title}</h2>
                    <p className="gx-p15-md">{limitContent.message}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="login-continue-btn">
                    <button className="gx-h4-eb" onClick={handleLimitContinue}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {companyLimitReached1 && (
          <>
            <div className="gx-smeonboard-box">
              <div className="gx-companyaccess-box">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12">
                    <div className="gx-request-progress-title">
                      <h2 className="gx-h2-l">Lets Confirm</h2>
                      <span className="gx-h2-b">Your Company Access</span>
                      <p className="gx-p12-m">
                        You'll need your GrowX Company ID to continue
                      </p>
                    </div>
                  </div>

                  <div className="col-span-12">
                    <CompanyID
                      label="Company ID"
                      value={companyId}
                      onChange={(val) => {
                        setCompanyId(val);
                        if (companyLimitReached) setCompanyLimitReached(false);
                      }}
                      validationMessage={error}
                      disable
                    />

                    {success && (
                      <div className="gx_input_successmessage">{success}</div>
                    )}
                  </div>

                  {!success && (
                    <div className="col-span-12">
                      <div className="growx-company-box">
                        <h4 className="gx-h4-sb">
                          Don't have your GrowX Company ID?
                        </h4>
                        <p className="gx-p12-m">
                          Please enquire with your Company Owner or Manager to
                          get your Company ID or if this is first time
                          onboarding to
                        </p>
                        <ContactSupportContent variant="inline" />
                      </div>
                    </div>
                  )}

                  <div className="col-span-12 mt-3">
                    <div className="col-span-12 mt-3">
                      <div className="gx-button-disple">
                        <button className="gx-h4-eb">Continue</button>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-12">
                    <div className="gx-connectbank-logout">
                      <button onClick={handleLogout}>
                        <img
                          src={`${ICON_BASE_PATH}logouticon.svg`}
                          alt="name"
                        />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyAccess;
